import {action, observable} from "mobx";
import {Nullable} from "../../../common/types/nullable-types";
import {IFirebaseTopicRegistration} from "../firebase/topics/firebase-topic-view-model.interface";
import {IUserTicketsUpdateData} from "../firebase/topics/in-game-challenges/tickets-update/user-tickets-update-topic.model.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {ServiceBase} from "../ServiceBase";
import {IInGameTicketService} from "./in-game-ticket.service.interface";
import {TicketModel} from "./ticket.model";

export class InGameTicketService extends ServiceBase implements IInGameTicketService {
    @observable.ref
    public availableTickets: TicketModel[] = [];

    constructor(services: IServiceFactoryExtended) {
        super(services);

        this.reaction(() => this.services.user.isAuthorized,
            (isAuth) => {
                this.dispose();
                if (isAuth) {
                    this._registerToTicketsUpdates()
                }
            }, {
            fireImmediately: true
        });
    }

    private _firebaseTicketsRegistration: Nullable<IFirebaseTopicRegistration> = null;

    private _registerToTicketsUpdates() {
        this._firebaseTicketsRegistration = this.services.firebase.topics.userTicketsUpdate.registerListener(this._handleTicketsUpdate.bind(this));
        this.services.logger.debug(`InGame Challenge: registered for tickets update.`);
    }

    @action
    private _handleTicketsUpdate(ticketUpdate: IUserTicketsUpdateData) {
        this.services.logger.debug(`InGame Challenge: recieved tickets update: ${ticketUpdate}`);
        // first, remove from available tickets all tickets ids that are no longer in the update response
        // keep only the ones existing also in ticketUpdate.tickets
        const newTicketsModelList = this.availableTickets.filter(ticketModel => {
            return ticketUpdate.tickets.find((rawTicket) => rawTicket.ticketId === ticketModel.ticketData.ticketId) !== undefined
        });
        // second, from the new update only take new tickets
        const newTickets = ticketUpdate.tickets.filter(rawTicket => {
            return this.availableTickets.find((ticketModel) => rawTicket.ticketId === ticketModel.ticketData.ticketId) === undefined
        }).map((ticket) => new TicketModel(ticket, this.services.api, this.services.logger));

        // merge them, filter the expired and order them by expiration date
        this.availableTickets = [...newTicketsModelList, ...newTickets].filter(ticket => !ticket.isExpired).sort((a, b) => {
            return new Date(b.ticketData.expirationDate).getMilliseconds() - new Date(a.ticketData.expirationDate).getMilliseconds()
        });

        // last but not least, in preparation for the magic 8 ball mini game, take the first ticket and fetch the possible prizes
        if (this.availableTickets.length > 0) {
            // we don't need to return anything, the result will be cached when the promise fullfils
            this.availableTickets[0].getPrizes();
        }

        this.services.logger.debug(`InGame Challenge: tickets update has ${this.availableTickets.length} tickets available`);
    }

    public getTicket() {
        if (this.availableTickets.length > 0) {
            return this.availableTickets[0];
        }
        return null;
    }

    public removeClaimedTickets() {
        this.availableTickets = this.availableTickets.filter((t) => t.claimed === false);
    }

    dispose(): void {
        this._firebaseTicketsRegistration?.unregister();
    }
}